import * as api from '../../utils/api/svsApi';
import * as selectors from './selectors';

import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { changeView, fetchSvs, fetchSvsError, fetchSvsList, fetchSvsListError, fetchSvsListSuccess, fetchSvsSuccess,
  resAdditionalSuccess, setSvsId, setSvsLines, updateSigning, setTabAsSaved, fetchSvsValidate, setSvsLocked } from './actions';

import { DetailsSvsActionTypes } from './types';
import { SvsViews } from './model';
import { validateSvsList } from '../overview/actions';
import { SagaIterator } from 'redux-saga';

function* handleFetchSvsList(action: ReturnType<typeof fetchSvsList>): SagaIterator {
  try {
    const [resSvsList, resAdditional] = yield all([
      call(api.getSvsList, action.payload.instructionId),
      call(api.getSvsAdditionalData, action.payload.instructionId)
    ]);

    if (!resSvsList.data) {
      yield put(fetchSvsListError(JSON.stringify(resSvsList.response)));
    } else if (!resAdditional.data) {
      yield put(fetchSvsListError(JSON.stringify(resAdditional.response)));
    } else {
      yield put(fetchSvsListSuccess(resSvsList.data));
      yield put(resAdditionalSuccess(resAdditional.data));
      if (resSvsList.data.length > 0) {
        const svsId = yield select(selectors.svsId);
        if (svsId > 0) {
        yield put(setSvsId(svsId));
        yield put(fetchSvs(svsId));
        } else {
          yield put(setSvsId(resSvsList.data[0].id));
          yield put(fetchSvs(resSvsList.data[0].id));
        }
      }
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchSvsListError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(fetchSvsListError('An unknown error occured.'));
    }
  }
}

function* handleFetchSvs(action: ReturnType<typeof fetchSvs>): SagaIterator {
  try {
    if (action.payload.id === 0) { return; } // Will be handled inside reducer

    const res = yield call(api.getSvs, action.payload.id);

    if (!res.data) {
      yield put(fetchSvsError(JSON.stringify(res.response)));
    } else {
      yield put(fetchSvsSuccess(res.data));
      yield put(fetchSvsValidate());
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchSvsError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(fetchSvsError('An unknown error occured.'));
    }
  }
}

function* handleChangeView(action: ReturnType<typeof changeView>): SagaIterator {
  const currentView = yield select(selectors.previousView);
  const savingBlocked = yield select(selectors.caseBlocked);
  const instructionId = yield select(selectors.instructionId);
  let result: any = {};

  if (currentView === action.payload.view) { return; }

  let svsId = yield select(selectors.svsId);

  switch (currentView) {
    case SvsViews.valueAndCause: {
      const isDirty = yield select(selectors.svsTabValueAndCauseIsDirty);
      if (!isDirty) { break; }

      const data = yield select(selectors.valueAndCause);
      if (!savingBlocked) {
        result = yield call(api.saveValueAndCause, svsId, instructionId, data);
      }
      if (!svsId) {
        svsId = result.data.id;
        yield put(fetchSvs(svsId));
      }
      yield put(setSvsLines(result.data));
      yield put(setTabAsSaved(currentView));
      break;
    }
    case SvsViews.vatAndPayment: {
      const isDirty = yield select(selectors.svsTabVatAndPaymentIsDirty);
      if (!isDirty) { break; }

      const data = yield select(selectors.vatAndPayment);
      if (!savingBlocked) {
         result = yield call(api.saveVatAndPayment, svsId, instructionId, data);
      }
      if (!svsId) {
        svsId = result.data.id;
        yield put(fetchSvs(svsId));
      }
      yield put(setTabAsSaved(currentView));
      break;
    }
    case SvsViews.svsLines: {
      const isDirty = yield select(selectors.svsTabLinesIsDirty);
      if (!isDirty) { break; }

      const data = yield select(selectors.svsLines);
      const excessApplicable = yield select(selectors.excessApplicable);
      const underCondition = yield select(selectors.underCondition);
      if (!savingBlocked) {
        result = yield call(api.saveSvsLines, svsId, instructionId, excessApplicable, underCondition, data);
      }
      if (!svsId) {
        svsId = result.data.id;
        yield put(fetchSvs(svsId));
      }
      yield put(setSvsLines(result.data));
      yield put(setTabAsSaved(currentView));
      break;
    }
    case SvsViews.counterExpertise: {
      const isDirty = yield select(selectors.svsTabCounterExpertiseIsDirty);
      if (!isDirty) { break; }

      const data = yield select(selectors.counterExpertise);

      if (!savingBlocked) {
        result = yield call(api.saveCounterExpertise, svsId, instructionId, data);
      }
      if (!svsId) {
        svsId = result.data.id;
        yield put(fetchSvs(svsId));
      }
      yield put(setTabAsSaved(currentView));
      break;
    }
    case SvsViews.communication: {
      const isDirty = yield select(selectors.svsTabCommunicationIsDirty);
      if (!isDirty) { break; }

      const data = yield select(selectors.communication);
      if (!savingBlocked) {
        result = yield call(api.saveCommunication, svsId, instructionId, data);
      }
      if (!svsId) {
        svsId = result.data.id;
        yield put(fetchSvs(svsId));
      }
      yield put(setTabAsSaved(currentView));
      break;
    }
    case SvsViews.remark: {
      const isDirty = yield select(selectors.svsTabRemarkIsDirty);
      if (!isDirty) { break; }

      const data = yield select(selectors.remark);
      if (!savingBlocked) {
        result = yield call(api.saveRemark, svsId, instructionId, data);
      }
      if (!svsId) {
        svsId = result.data.id;
        yield put(fetchSvs(svsId));
      }
      yield put(setTabAsSaved(currentView));
      break;
    }
    case SvsViews.signing: {
      const isDirty = yield select(selectors.svsTabSigningIsDirty);
      if (!isDirty) { break; }

      const data = yield select(selectors.signing);
      const locked = yield select(selectors.locked);
      if (!savingBlocked) {
        result = yield call(api.saveSigning, svsId, instructionId, data, locked);
      }
      if (!svsId) {
        svsId = result.data.id;
        yield put(fetchSvs(svsId));
      }
      yield put(setTabAsSaved(currentView));
      break;
    }
  }

  yield put(fetchSvsList(instructionId));
  yield put(setSvsId(svsId));
}

function* handleUpdateSigning(action: ReturnType<typeof updateSigning>): SagaIterator {
  const svsId = yield select(selectors.svsId);
  const instructionId = yield select(selectors.instructionId);

  const savingBlocked = yield select(selectors.caseBlocked);
  let result: any = {};
  let newId: number = svsId;

  const data = yield select(selectors.signing);

  if (!savingBlocked) {
    result = yield call(api.saveSigning, svsId, instructionId, data, true);
    newId = result.data.id;
  }

  yield put(setSvsId(newId));
  yield put(setSvsLocked());

  const existingSvsItem = yield select(selectors.svsItems, newId);

  if (!existingSvsItem) {
    yield put(fetchSvsList(instructionId));
  }
}

function* handleGetSvsListSuccess(): SagaIterator {
  const svsIds = yield select(selectors.svsIds);

  yield put(validateSvsList(svsIds));
}

function* watchSvs() {
  yield takeEvery(DetailsSvsActionTypes.FETCH_SVS_LIST, handleFetchSvsList);
  yield takeEvery(DetailsSvsActionTypes.FETCH_SVS, handleFetchSvs);
  yield takeEvery(DetailsSvsActionTypes.CHANGE_VIEW, handleChangeView);
  yield takeEvery(DetailsSvsActionTypes.UPDATE_SIGNING, handleUpdateSigning);
  yield takeEvery(DetailsSvsActionTypes.FETCH_SVS_LIST_SUCCESS, handleGetSvsListSuccess);
}

function* svsSaga() {
  yield all([fork(watchSvs)]);
}

export default svsSaga;
